<app-uni-page [parent]="this" [pageTitle]="title" [bodyTemplate]="bodyTemplate" [footerTemplate]="footerTemplate"></app-uni-page>

<ng-template #bodyTemplate>
  <!-- Manufacturer feed banner. Only shown when the manufacturer actually has a feed. -->
  <ng-container *ngIf="feedHealth as h">
    <div class="mx-3 mt-2 mb-1 px-4 py-2 qb-detail-card qb-primary-tint flex items-center gap-3">
      <i class="pi pi-cloud-download"></i>
      <span *ngIf="!h.IsCustomStyle" class="font-semibold">The {{ h.ManufacturerName }} feed is available</span>
      <span *ngIf="h.IsCustomStyle" class="font-semibold">This is a custom style</span>
      <span class="text-sm" style="color: var(--p-text-muted-color, #6b7280)">
        <ng-container *ngIf="h.IsCustomStyle"> It does not use the {{ h.ManufacturerName }} feed. </ng-container>
        <ng-container *ngIf="!h.IsCustomStyle && !h.IsStyleStaged"> The manufacturer has not returned this style. </ng-container>
      </span>
      <!-- Gatekeeper F4 signal, C2 Convert to Feed. -->
      <span *ngIf="h.IsCustomStyle && h.FeedHasThisStyle" class="text-sm font-semibold" style="color: var(--p-primary-color, var(--p-primary-500))">
        The {{ h.ManufacturerName }} feed now has this style.
      </span>
      <button
        *ngIf="h.IsCustomStyle && h.FeedHasThisStyle && !isAdding"
        pButton
        type="button"
        class="p-button-raised p-button-sm ml-auto"
        icon="pi pi-cloud-download"
        [disabled]="!user.isProductAdmin"
        (click)="onConvertToFeed()"
      >
        <span>Convert to Feed</span>
      </button>
    </div>
  </ng-container>

  <!-- Gatekeeper C2: this style is one half of a conversion to a feed style. -->
  <ng-container *ngIf="conversionPartner as partner">
    <div class="mx-3 mt-2 mb-1 px-4 py-2 qb-detail-card qb-primary-tint flex items-center gap-3">
      <i class="pi pi-arrow-right-arrow-left"></i>
      <span *ngIf="isConversionOld" class="font-semibold">The feed style {{ partner.style }} replaces this style.</span>
      <span *ngIf="!isConversionOld" class="font-semibold">This feed style replaces style {{ partner.style }}.</span>
      <span class="text-sm" style="color: var(--p-text-muted-color, #6b7280)">
        {{ conversionConfirmedCount }} of {{ conversionOldProductCount }} old products are mapped and confirmed.
      </span>
      <button pButton type="button" class="p-button-raised p-button-sm ml-auto" icon="pi pi-external-link" (click)="openConversionPartner()">
        <span>Open {{ partner.style }}</span>
      </button>
      <button
        pButton
        type="button"
        class="p-button-raised p-button-sm"
        icon="pi pi-sitemap"
        [disabled]="!user.isProductAdmin"
        (click)="onMapOldProducts()"
      >
        <span>Map Old Products</span>
      </button>
    </div>
  </ng-container>

  <!-- Before the feed has answered about a SKU. Once it has, the banner above takes over because
       it can speak about the actual style; this one says what is about to happen, or what the
       check just said. -->
  <ng-container *ngIf="!feedHealth && feedManufacturer as mfr">
    <div class="mx-3 mt-2 mb-1 px-4 py-2 qb-detail-card qb-primary-tint flex items-center gap-3">
      <i class="pi" [ngClass]="isCheckingStyle ? 'pi-spin pi-spinner' : 'pi-cloud-download'"></i>
      <span class="font-semibold">{{ mfr.name }} has a manufacturer feed</span>
      <span class="text-sm" style="color: var(--p-text-muted-color, #6b7280)">
        <ng-container *ngIf="isCheckingStyle">The system asks {{ mfr.name }} about this SKU.</ng-container>
        <ng-container *ngIf="!isCheckingStyle && styleCheckError">
          <span style="color: var(--p-red-500, #ef4444)">{{ styleCheckError }}</span>
        </ng-container>
        <ng-container *ngIf="!isCheckingStyle && !styleCheckError">
          The system checks the SKU with {{ mfr.name }} when you leave the SKU field. A style
          {{ mfr.name }} does not publish cannot be added.
        </ng-container>
      </span>
    </div>
  </ng-container>

  <div class="mb-2"></div>
  <p-splitter *ngIf="isPageReady" class="grow w-full" layout="vertical" styleClass="h-full" [panelSizes]="[60, 40]">
    <ng-template pTemplate>
      <div class="flex flex-row gap-3 p-3 w-full h-full">
        <!-- Left Panel: Main Form Fields -->
        <div class="qb-detail-card w-1/2 overflow-auto">
          <!-- Row 1: Manufacturer -->
          <div class="grid grid-cols-12 gap-3 mb-3">
            <qb-field label="Manufacturer" class="col-span-6" [hint]="isFormLocked ? 'Select a manufacturer to start' : ''">
              <input pInputText [ngModel]="productType.manufacturer?.name" disabled="true" />
            </qb-field>
            <div class="col-span-2 flex items-end pb-1">
              <button *ngIf="isAdding" (click)="selectManufacturer()" pButton type="button" class="p-button-raised p-button-sm w-full">
                <span>Select</span>
              </button>
            </div>
          </div>
          <!-- Row 2: SKU & Status -->
          <div class="grid grid-cols-12 gap-3 mb-3">
            <qb-field label="SKU" class="col-span-5">
              <div class="flex items-center gap-2">
                <!-- Shown in capitals as it is typed, and settled in the model on blur, so what
                     is on screen is what gets saved rather than a transform that arrives later. -->
                <input
                  #firstInput
                  class="flex-1"
                  style="text-transform: uppercase"
                  [disabled]="!canEditStyle || isMeaningLocked || isFeedLocked"
                  pInputText
                  [bzModel]="productType"
                  name="style"
                  [(ngModel)]="productType.style"
                  title="SKU"
                  (blur)="onStyleBlur()"
                  (keyup.enter)="onStyleBlur()"
                />
                <i *ngIf="isCheckingStyle" class="pi pi-spin pi-spinner" style="color: var(--p-primary-color)"></i>
              </div>
              <!-- The verdict belongs next to the field that caused it, not in a dialog that
                   has to be dismissed before the SKU can be corrected. -->
              <small *ngIf="isMeaningLocked && !isFeedLocked" style="color: var(--p-orange-600, #ea580c)">
                This style has products that were used on orders. You cannot change its SKU. Make a new style.
              </small>
              <small *ngIf="isFeedLocked" style="color: var(--p-orange-600, #ea580c)">
                This style uses the feed. You cannot change its SKU. If the manufacturer has a new style, make a new style.
              </small>
              <small *ngIf="isCheckingStyle" style="color: var(--p-text-muted-color, #6b7280)">
                The system asks {{ feedManufacturer?.name }} about this SKU.
              </small>
              <small *ngIf="!isCheckingStyle && styleCheckError" style="color: var(--p-red-500, #ef4444)">
                {{ styleCheckError }}
              </small>
              <small *ngIf="!isCheckingStyle && styleCheckWarning" style="color: var(--p-orange-500, #f59e0b)">
                {{ styleCheckWarning }}
              </small>
              <small
                *ngIf="!isCheckingStyle && !styleCheckError && !styleCheckWarning && feedHealth?.IsStyleStaged"
                style="color: var(--p-green-600, #16a34a)"
              >
                {{ feedManufacturer?.name }} publishes this SKU.
              </small>
            </qb-field>
            <qb-field label="Status" class="col-span-2">
              <p-dropdown
                [options]="activeStatuses"
                [disabled]="!canEdit"
                [(ngModel)]="productType.activeStatusId"
                (onChange)="onStatusChange()"
                optionValue="id"
                optionLabel="name"
              />
            </qb-field>
          </div>

          <!-- Row 3: Description -->
          <div class="grid grid-cols-12 gap-3 mb-3">
            <qb-field label="Description" [class]="feedHealth?.IsStyleStaged ? 'col-span-9' : 'col-span-12'">
              <input
                [disabled]="!canEdit"
                pInputText
                [bzModel]="productType"
                name="description"
                [(ngModel)]="productType.description"
              />
            </qb-field>
            <div *ngIf="feedHealth?.IsStyleStaged" class="col-span-3 flex items-end pb-1">
              <button
                pButton
                type="button"
                class="p-button-raised p-button-sm w-full"
                icon="pi pi-cloud-download"
                [loading]="isUpdatingDescription"
                [disabled]="!canUpdateDescriptionFromFeed"
                (click)="onUpdateDescriptionFromFeed()"
              >
                <span>Update Ext. Description</span>
              </button>
            </div>
          </div>
          <!-- Row 3: Extended Description -->
          <div class="grid grid-cols-12 gap-3 mb-3">
            <qb-field label="Extended Description" class="col-span-12">
              <uni-html-editor
                [(value)]="productType.descriptionExt"
                [isReadOnly]="!canEdit"
                style="display: block; height: 150px"
              ></uni-html-editor>
            </qb-field>
          </div>
          <!-- Row 4: EOQ Settings -->
          <div class="grid grid-cols-12 gap-2">
            <qb-field label="Lead Time (days)" labelClass="w-full text-right" class="col-span-2">
              <input
                [disabled]="!canEdit"
                type="number"
                pInputText
                [bzModel]="productType"
                name="leadTimeDays"
                [(ngModel)]="productType.leadTimeDays"
                style="text-align: right"
              />
            </qb-field>
            <qb-field label="EOQ Type" class="col-span-2">
              <p-dropdown
                [options]="eOQTypes"
                [disabled]="!canEdit"
                [(ngModel)]="productType.eOQTypeId"
                optionValue="id"
                optionLabel="name"
              />
            </qb-field>
            <qb-field label="Ordering Cost" labelClass="w-full text-right" class="col-span-2">
              <input
                [disabled]="!canEdit"
                pInputText
                currencyMask
                [bzModel]="productType"
                name="orderingCostAmt"
                [(ngModel)]="productType.orderingCostAmt"
                style="text-align: right"
              />
            </qb-field>
            <qb-field label="Carrying Cost/Year" labelClass="w-full text-right" class="col-span-2">
              <input
                [disabled]="!canEdit"
                pInputText
                currencyMask
                [bzModel]="productType"
                name="unitCarryingCostPerYearAmt"
                [(ngModel)]="productType.unitCarryingCostPerYearAmt"
                style="text-align: right"
              />
            </qb-field>
            <qb-field label="Safety Stock %" labelClass="w-full text-right" class="col-span-2">
              <input
                [disabled]="!canEdit"
                pInputText
                type="number"
                min="0"
                max="100"
                style="text-align: right"
                [bzModel]="productType"
                name="safetyStockPerYearPct"
                [ngModel]="productType.safetyStockPerYearPctFormatted | number: '1.2-2'"
                (ngModelChange)="productType.safetyStockPerYearPctFormatted = $event"
              />
            </qb-field>
            <!-- Images come from the manufacturer once it owns the catalog, so uploading here
                 would only create something the next feed read contradicts. -->
            <div class="col-span-3 flex flex-col justify-end pb-2">
              <app-image-handler
                *ngIf="!areImagesFeedOwned"
                [isReadOnly]="!canEdit"
                [oldImages]="productType.productTypeImages"
                (createJoinEntity)="createImageJoinEntity($event)"
              ></app-image-handler>
              <span *ngIf="areImagesFeedOwned" class="text-xs mt-1" style="color: var(--p-text-muted-color, #6b7280)">
                Images are managed by the Manufacturer Feed.
              </span>
            </div>
          </div>
        </div>
        <!-- Right Panel: Features -->
        <div class="w-1/2 h-full">
          <div class="qb-detail-card w-full overflow-auto">
            <h6 class="mb-3 font-semibold">Features</h6>

            <!-- Feature Actions -->
            <div class="flex flex-row flex-wrap gap-2 items-center mb-3">
              <button
                pButton
                type="button"
                class="p-button-raised p-button-sm"
                icon="pi pi-plus"
                [disabled]="!canEdit || isFeatureBrakeLocked || isFeedFeatureLocked"
                (click)="onAddFeature()"
              >
                <span>Add</span>
              </button>
              <button
                pButton
                type="button"
                class="p-button-raised p-button-sm"
                icon="pi pi-pencil"
                [disabled]="!canEdit || isMeaningLocked || isFeedFeatureLocked"
                (click)="onChangeFeatures()"
              >
                <span>Change</span>
              </button>
            </div>
            <div *ngIf="isFeedLocked" class="text-sm mb-3" style="color: var(--p-orange-600, #ea580c)">
              This style uses the feed. You can add products, and put products on Phaseout or Inactive. You cannot change
              the features or the choices of its products. If a product is wrong, put it on Phaseout. Then build the right product.
            </div>
            <div *ngIf="isFeedFeatureLocked && !isFeedLocked" class="text-sm mb-3" style="color: var(--p-orange-600, #ea580c)">
              This style uses the feed. Its features come from the feed. You cannot add or change a feature.
            </div>
            <div *ngIf="isFeatureBrakeLocked && !isFeedFeatureLocked" class="text-sm mb-3" style="color: var(--p-orange-600, #ea580c)">
              This style has products that were used on orders. You cannot
              {{ isMeaningLocked ? 'add, remove, or change a feature' : 'add or remove a feature' }}. Make a new style with the
              features you need.
            </div>
            <!-- Use Reorder Checkbox -->
            <div class="flex items-center gap-2 mb-3">
              <p-checkbox
                inputId="allowReorder"
                [disabled]="!canEdit"
                [binary]="true"
                [(ngModel)]="productType.allowReorderFeatureChoices"
                (onChange)="onAllowReorderFeatureChange()"
              />
              <label for="allowReorder">Use Reorder Feature Choices</label>
            </div>
            <!-- Features List -->
            <div class="flex flex-col gap-2">
              <ng-container *ngFor="let map of productType.getOrderedProductTypeFeatureMaps()">
                <div class="flex flex-row gap-2 items-end">
                  <qb-field label="Feature" class="flex-1">
                    <input disabled="true" pInputText [ngModel]="map.feature.name" />
                  </qb-field>
                  <qb-field label="Order" class="w-16">
                    <input
                      [disabled]="!productType.allowReorderFeatureChoices"
                      pInputText
                      [ngModel]="map.displayOrder"
                      (ngModelChange)="onDisplayOrderChanged(map, $event)"
                      [ngModelOptions]="{ updateOn: 'blur' }"
                    />
                  </qb-field>
                  <qb-field label="&nbsp;">
                    <a
                      class="leading-10"
                      [class.cursor-pointer]="!uow.hasChanges()"
                      [class.text-primary]="!uow.hasChanges()"
                      [class.text-gray-400]="uow.hasChanges()"
                      (click)="!uow.hasChanges() && viewFeature(map.feature)"
                      >View</a
                    >
                  </qb-field>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- What is in flight on this style's products, in Uniformax and Proximity. Information only. -->
          <div *ngIf="!isAdding && productType?.id" class="mt-3">
            <app-product-exposure-panel [productTypeId]="productType.id" (loaded)="productUse = $event"></app-product-exposure-panel>
          </div>

          <!-- Feed Health. Whether this style is aligned with the manufacturer, and what is not. -->
          <div *ngIf="feedHealth as h" class="qb-detail-card qb-primary-tint mt-3 overflow-auto">
            <!-- Closed by default: the header line carries the verdict. -->
            <div
              class="flex items-center gap-2 cursor-pointer select-none"
              [class.mb-3]="isFeedHealthOpen"
              title="Show or hide the Feed Health details"
              (click)="isFeedHealthOpen = !isFeedHealthOpen"
            >
              <i class="pi text-xs" [ngClass]="isFeedHealthOpen ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              <h6 class="font-semibold m-0">Feed Health</h6>
              <!-- On Add the verdicts are hidden, so a closed card must still say a save would be refused. -->
              <span
                *ngIf="isAdding && h.FeatureShapeGaps.length"
                class="text-sm font-semibold"
                style="color: var(--p-red-500, #ef4444)"
                >{{ h.FeatureShapeGaps.length }} required feature{{ h.FeatureShapeGaps.length === 1 ? ' is' : 's are' }} missing.</span
              >
              <!-- Off, not failing. An inactive product type sells nothing, so neither verdict
                   would mean anything about it. -->
              <span
                *ngIf="!isAdding && h.IsProductTypeInactive"
                class="text-sm font-semibold"
                style="color: var(--p-text-muted-color, #6b7280)"
                >Disabled</span
              >
              <span
                *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsHealthy"
                class="text-sm font-semibold"
                style="color: var(--p-green-600, #16a34a)"
                >&#10003; Aligned with the manufacturer</span
              >
              <!-- Closed, the card must still say the feed has colors to add; open, Check 1 says it. -->
              <span
                *ngIf="!isFeedHealthOpen && !isAdding && !h.IsProductTypeInactive && h.IsStyleStaged && h.FeedColorsMissingFromOurList.length"
                class="text-sm"
                style="color: var(--p-text-muted-color, #6b7280)"
                >({{ h.FeedColorsMissingFromOurList.length }} more color{{ h.FeedColorsMissingFromOurList.length === 1 ? ' is' : 's are' }} available)</span
              >
              <!-- Closed, the card must still say feed sizes wait for a match; open, the sizes check says it. -->
              <span
                *ngIf="!isFeedHealthOpen && !isAdding && !h.IsProductTypeInactive && h.IsStyleStaged && h.HasSizeFeature && h.FeedSizesWeCannotBuild.length"
                class="text-sm"
                style="color: var(--p-text-muted-color, #6b7280)"
                >({{ h.FeedSizesWeCannotBuild.length }} size{{ h.FeedSizesWeCannotBuild.length === 1 ? ' must' : 's must' }} be matched)</span
              >
              <!-- A custom style is not compared with the feed, so it is never "not aligned" for that.
                   A missing required feature still is: that rule holds for custom styles too. -->
              <span
                *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsCustomStyle"
                class="text-sm font-semibold"
                style="color: var(--p-text-muted-color, #6b7280)"
                >Custom style</span
              >
              <span
                *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsCustomStyle && h.FeedHasThisStyle"
                class="text-sm font-semibold"
                style="color: var(--p-primary-color, var(--p-primary-500))"
                >(the feed now has this style)</span
              >
              <span
                *ngIf="!isAdding && !h.IsProductTypeInactive && !h.IsHealthy && (!h.IsCustomStyle || h.FeatureShapeGaps.length)"
                class="text-sm font-semibold"
                style="color: var(--p-red-500, #ef4444)"
              >
                Not yet aligned
              </span>
              <!-- When the feed was last read for THIS style, not when Uniformax last changed. -->
              <span *ngIf="!isAdding && h.SnapshotTs" class="ml-auto text-sm" style="color: var(--p-text-muted-color, #6b7280)">
                Feed last read {{ h.SnapshotTs | date: 'short' }}
              </span>
            </div>

            <ng-container *ngIf="isFeedHealthOpen">
            <!-- What the MANUFACTURER calls this style, as of the last good read. Read-only, and
                 deliberately not the Description on the left: that is our own catalogue label.
                 From the staged row via Feed Health, never from a live call to the manufacturer. -->
            <div *ngIf="h.FeedDescription" class="flex items-baseline gap-2 mb-3 text-sm">
              <span class="font-semibold shrink-0" style="color: var(--p-text-muted-color, #6b7280)">
                {{ h.ManufacturerName }} calls it
              </span>
              <span>{{ h.FeedDescription }}</span>
            </div>

            <!-- A style being added is not a style yet. Every check below compares the manufacturer
                 against what Uniformax holds, and what Uniformax holds is an unsaved entity whose
                 features are still being chosen - so each verdict would be about a half-made thing.
                 The actions are worse than the verdicts: Update Colors writes FeatureChoices through
                 the shared entity manager and would drag the unfinished style to the server with
                 them, and Match Sizes queues parts the feed has not staged for a style that does
                 not exist. Saving first settles all of it in one state. -->
            <div *ngIf="isAdding" class="text-sm italic" style="color: var(--p-text-muted-color, #6b7280)">
              The system checks Feed Health after you save the style.
            </div>

            <div *ngIf="!isAdding && h.IsProductTypeInactive" class="text-sm italic" style="color: var(--p-text-muted-color, #6b7280)">
              This product type is inactive. The system does not check it against the manufacturer.
            </div>

            <!-- Shown whatever the feed says, because it does not depend on the feed. Without it a
                 style with no Color feature read "Aligned": it has no colors to find wrong. -->
            <!-- On Add too: it is judged on the features on screen, and saying it now beats the
                 save refusing it after the whole form is filled in. -->
            <div *ngIf="!h.IsProductTypeInactive && h.FeatureShapeGaps.length" class="flex flex-col gap-1 mb-3">
              <div class="text-sm font-semibold">
                Features the manufacturer requires
                <span style="color: var(--p-red-500, #ef4444)">({{ h.FeatureShapeGaps.length }})</span>
              </div>
              <div *ngFor="let g of h.FeatureShapeGaps" class="text-sm" style="color: var(--p-red-500, #ef4444)">
                {{ g.Message }}
              </div>
            </div>

            <!-- Checked, but on the last good read: the newest one failed for a reason that says
                 nothing about the style (StagedStyleFns). Said, so nobody takes it for today's data. -->
            <div
              *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsStyleStaged && h.FetchError"
              class="text-sm italic mb-3"
              style="color: var(--p-text-muted-color, #6b7280)"
            >
              The last read from the manufacturer failed. The checks use the data from
              {{ h.SnapshotTs | date: 'short' }}.
              <span style="color: var(--p-red-500, #ef4444)">{{ h.FetchError }}</span>
            </div>

            <div
              *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsCustomStyle"
              class="text-sm italic"
              style="color: var(--p-text-muted-color, #6b7280)"
            >
              This style does not use the feed, so nothing is compared with the feed.
              <ng-container *ngIf="h.FeedHasThisStyle">
                The {{ h.ManufacturerName }} feed now has a style with this name. Do not change this style to
                get the feed data. To make a feed style for it, select Convert to Feed.
              </ng-container>
            </div>

            <div
              *ngIf="!isAdding && !h.IsProductTypeInactive && !h.IsCustomStyle && !h.IsStyleStaged"
              class="text-sm italic"
              style="color: var(--p-text-muted-color, #6b7280)"
            >
              The manufacturer has not returned this style, so nothing can be compared.
              <span *ngIf="h.FetchError" style="color: var(--p-red-500, #ef4444)">{{ h.FetchError }}</span>
            </div>

            <!-- The stop. One style, one product type: the manufacturer answers once, so a second
                 instance gives every check below two answers and no way to choose between them.
                 Everything else is suppressed rather than reported against whichever was asked. -->
            <div *ngIf="!isAdding && h.DuplicateProductTypes.length" class="flex flex-col gap-2">
              <div class="text-sm font-semibold" style="color: var(--p-red-500, #ef4444)">
                This style has {{ h.DuplicateProductTypes.length }} product types. It must have one.
              </div>

              <table class="text-sm">
                <tr *ngFor="let d of h.DuplicateProductTypes">
                  <td class="pr-3 py-0.5 align-top">{{ d.ProductTypeId }}</td>
                  <td class="pr-3 py-0.5 align-top">{{ d.Description }}</td>
                  <td class="pr-3 py-0.5 align-top" style="color: var(--p-text-muted-color, #6b7280)">
                    {{ d.SizeFeatureName || 'no size list' }}
                  </td>
                  <td class="py-0.5 align-top" style="color: var(--p-text-muted-color, #6b7280)">
                    {{ d.ProductCount }} product{{ d.ProductCount === 1 ? '' : 's' }}
                  </td>
                </tr>
              </table>

              <div class="text-sm italic" style="color: var(--p-text-muted-color, #6b7280)">
                The manufacturer gives one answer for each style, so the system cannot check this
                style until only one product type is active. Move the products and the orders you
                want to keep, then make the other product types inactive.
              </div>
            </div>

            <div
              *ngIf="!isAdding && !h.IsProductTypeInactive && h.IsStyleStaged && !h.DuplicateProductTypes.length"
              class="flex flex-col gap-3"
            >
              <!-- Check 1 -->
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <!-- Informational, not a fault. Carrying fewer colors than the manufacturer
                       offers is the subset rule working, so this is never shown in red. -->
                  <div class="text-sm font-semibold">
                    Manufacturer colors not in the Uniformax color list
                    <span style="color: var(--p-text-muted-color, #6b7280)">
                      ({{ h.FeedColorsMissingFromOurList.length }})
                    </span>
                  </div>
                  <button
                    *ngIf="h.FeedColorsMissingFromOurList.length"
                    pButton
                    type="button"
                    class="p-button-raised p-button-sm ml-auto"
                    icon="pi pi-plus"
                    [disabled]="!this.user.isProductAdmin || isAdding"
                    (click)="onAddFeedColors()"
                  >
                    <span>Update Colors</span>
                  </button>
                </div>
                <div *ngIf="h.FeedColorsMissingFromOurList.length" class="text-sm">
                  {{ h.FeedColorsMissingFromOurList.join(', ') }}
                </div>
              </div>

              <!-- Check 2. Each row offers to re-point that color onto one the manufacturer
                   does publish, which is the fix for a color like 'S Orange'. -->
              <div>
                <div class="text-sm font-semibold mb-1">
                  Uniformax colors the manufacturer does not make
                  <span [style.color]="h.OurColorsNotInFeed.length ? 'var(--p-red-500, #ef4444)' : 'var(--p-green-600, #16a34a)'">
                    ({{ h.OurColorsNotInFeed.length }})
                  </span>
                </div>
                <div *ngFor="let c of h.OurColorsNotInFeed" class="flex items-center gap-3 text-sm py-1">
                  <span>{{ c.UniformaxColorName }}</span>
                  <span style="color: var(--p-text-muted-color, #6b7280)">
                    {{ c.ProductCount }} product{{ c.ProductCount === 1 ? '' : 's' }}
                  </span>
                  <a
                    class="cursor-pointer ml-auto"
                    [class.text-primary]="this.user.isProductAdmin && !isAdding && !isFeedLocked"
                    [class.text-gray-400]="!this.user.isProductAdmin || isAdding || isFeedLocked"
                    (click)="this.user.isProductAdmin && !isAdding && !isFeedLocked && onReassignColor(c)"
                    >Reassign Color</a
                  >
                </div>
              </div>

              <!-- Check 3. The only check that closes the Product Generator. A size the scale
                   cannot express cannot be built at all, which is a different thing from a color
                   we choose not to carry - that stays a purchasing decision. Judged on the feed's
                   own size text, never on the stored pointer; see FillSizeCoverageAsync. -->
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <div class="text-sm font-semibold">
                    Sizes the manufacturer makes that {{ h.SizeFeatureName || 'the size scale' }} cannot build
                    <span
                      *ngIf="h.HasSizeFeature"
                      [style.color]="h.FeedSizesWeCannotBuild.length ? 'var(--p-red-500, #ef4444)' : 'var(--p-green-600, #16a34a)'"
                    >
                      ({{ h.FeedSizesWeCannotBuild.length }})
                    </span>
                  </div>
                  <button
                    *ngIf="h.HasSizeFeature && h.FeedSizesWeCannotBuild.length"
                    pButton
                    type="button"
                    class="p-button-raised p-button-sm ml-auto"
                    icon="pi pi-sliders-h"
                    [disabled]="!this.user.isProductAdmin"
                    (click)="onMatchSizes()"
                  >
                    <span>Match Sizes</span>
                  </button>
                </div>

                <div *ngIf="!h.HasSizeFeature" class="text-sm italic" style="color: var(--p-text-muted-color, #6b7280)">
                  Attach a size feature to this style to check the sizes.
                </div>

                <div *ngIf="h.HasSizeFeature && h.FeedSizesWeCannotBuild.length" class="text-sm">
                  <span *ngFor="let s of h.FeedSizesWeCannotBuild; let last = last"
                    >{{ s.FeedSize }}<span *ngIf="s.ApparelStyle"> ({{ s.ApparelStyle }})</span>{{ last ? '' : ', ' }}</span
                  >
                </div>

                <!-- Answered, but for a size list this style does not use. Deliberately offers no
                     Match Sizes button: that screen queues unanswered parts only and will never
                     show these, so pointing at it would be a dead end. The fix is one product type
                     fewer on this style, which is said in words. -->
                <div *ngIf="h.FeedSizesOnAnotherScale.length" class="text-sm mt-2">
                  <div class="font-semibold mb-1">
                    Matched to a different size list
                    <span style="color: var(--p-red-500, #ef4444)">({{ h.FeedSizesOnAnotherScale.length }})</span>
                  </div>
                  <div *ngFor="let s of h.FeedSizesOnAnotherScale" class="py-0.5">
                    {{ s.FeedSize }}<span *ngIf="s.ApparelStyle"> ({{ s.ApparelStyle }})</span>
                    &rarr; {{ s.OnOtherScaleChoiceValue }}
                    <span style="color: var(--p-text-muted-color, #6b7280)">
                      on '{{ s.OnOtherScaleFeatureName }}'
                    </span>
                  </div>
                  <!-- Two causes, two remedies. A second live product type still holds the pointer,
                       so retiring it is the fix and Match Sizes cannot help. Once nothing else uses
                       the style the pointer is merely stale, and Match Sizes can answer it again -
                       which is why the queue now offers parts answered against another list. -->
                  <div *ngIf="h.OtherSizeScaleNames.length" class="mt-1 italic" style="color: var(--p-text-muted-color, #6b7280)">
                    More than one product type uses this style, with a different size list:
                    {{ h.OtherSizeScaleNames.join(', ') }}. The manufacturer records one size for
                    each part, so only one list can hold the answer. Retire the product type you do
                    not want.
                  </div>

                  <div *ngIf="!h.OtherSizeScaleNames.length" class="flex items-center gap-2 mt-1">
                    <span class="italic" style="color: var(--p-text-muted-color, #6b7280)">
                      These sizes are matched to a size list this style does not use. Match them
                      again to build this style.
                    </span>
                    <button
                      pButton
                      type="button"
                      class="p-button-raised p-button-sm ml-auto"
                      icon="pi pi-sliders-h"
                      [disabled]="!this.user.isProductAdmin"
                      (click)="onMatchSizes()"
                    >
                      <span>Match Sizes</span>
                    </button>
                  </div>
                </div>

                <!-- Blocks nothing: these build. On screen because a wrong hand mapping is
                     invisible from everywhere else, and a size mapped onto a choice of another
                     name is the only shape in which a wrong answer still builds.

                     Each one is clickable, because this panel is also the only place the mistake
                     can be CORRECTED from. Match Sizes queues sizes with no answer, and these are
                     answered - so without a way in from here, a wrong answer is permanent short of
                     a hand-written update. The click asks that screen for this size back. -->
                <div *ngIf="h.FeedSizesAnsweredByHand.length" class="text-sm mt-1">
                  <span style="color: var(--p-text-muted-color, #6b7280)">Matched by hand:</span>
                  <span *ngFor="let s of h.FeedSizesAnsweredByHand; let last = last">
                    <a
                      [class.cursor-pointer]="this.user.isProductAdmin && !isAdding"
                      [style.color]="
                        s.MappingDisagreesWithName
                          ? 'var(--p-red-500, #ef4444)'
                          : 'var(--p-text-muted-color, #6b7280)'
                      "
                      [style.text-decoration]="
                        this.user.isProductAdmin && !isAdding ? 'underline dotted' : 'none'
                      "
                      [title]="
                        s.MappingDisagreesWithName
                          ? 'The size scale has a choice named ' +
                            s.FeedSize +
                            '. This mapping points at ' +
                            s.MappedChoiceValue +
                            ' instead. Select it to change the answer.'
                          : 'Select this size to change what it means.'
                      "
                      (click)="this.user.isProductAdmin && !isAdding && onChangeHandMatch(s)"
                      >{{ s.FeedSize }} &rarr; {{ s.MappedChoiceValue }}</a
                    >{{ last ? '' : ', ' }}
                  </span>
                </div>
              </div>

              <!-- Check 4. The size half of the one rule health actually enforces: we must not
                   offer what the manufacturer does not make. Judged on the color-and-size pair,
                   because a size real in one color can be absent in the next. -->
              <div>
                <div class="text-sm font-semibold mb-1">
                  {{ h.FeedListsWholeCatalog ? "Products the manufacturer does not make" : "Products not in the supplier's file" }}
                  <span
                    [style.color]="
                      !h.OurProductsNotInFeed.length ? 'var(--p-green-600, #16a34a)'
                      : h.FeedListsWholeCatalog ? 'var(--p-red-500, #ef4444)'
                      : 'var(--p-text-muted-color, #6b7280)'
                    "
                  >
                    ({{ h.OurProductsNotInFeed.length }})
                  </span>
                </div>

                <!-- A stock file lists only what the supplier stocks, so absence proves nothing. -->
                <div *ngIf="h.OurProductsNotInFeed.length && !h.FeedListsWholeCatalog" class="text-sm italic mb-1"
                     style="color: var(--p-text-muted-color, #6b7280)">
                  The file lists only stocked items. Ask the supplier about these before you retire them.
                </div>
                <!-- No color on a size-only manufacturer such as Red Kap, so no "Color /" in front. -->
                <div *ngIf="h.OurProductsNotInFeed.length" class="text-sm">
                  <span *ngFor="let p of h.OurProductsNotInFeed; let last = last"
                    >{{ p.ColorName ? p.ColorName + ' / ' : '' }}{{ p.SizeName }}{{ last ? '' : ', ' }}</span
                  >
                </div>

                <!-- A finding, and counted. The feed can never publish a retired choice, so these are
                     not on sale as anything the manufacturer makes. They used to vanish from the check. -->
                <div *ngIf="h.OurProductsOnRetiredChoices.length" class="text-sm mt-1">
                  <span style="color: var(--p-red-500, #ef4444)">
                    {{ h.OurProductsOnRetiredChoices.length }} product{{ h.OurProductsOnRetiredChoices.length === 1 ? '' : 's' }}
                    use an inactive size or color:
                  </span>
                  <span *ngFor="let p of h.OurProductsOnRetiredChoices; let last = last"
                    >{{ p.ColorName ? p.ColorName + ' / ' : '' }}{{ p.SizeName }}{{ last ? '' : ', ' }}</span
                  >
                </div>

                <!-- Not a fault and deliberately not counted: the question cannot be answered yet - our
                     7X may well be their 7XL. The server names the reason, because unanswered sizes
                     are no longer the only one (a missing feature, a product with no size choice). -->
                <div
                  *ngIf="h.ProductsNotCheckable"
                  class="text-sm mt-1"
                  style="color: var(--p-text-muted-color, #6b7280)"
                >
                  {{ h.ProductsNotCheckableReason }}
                </div>
              </div>

              <!-- Check 5. Also informational: wording drift does not affect what can be
                   ordered, so it is reported but never counts against health. -->
              <div>
                <div class="text-sm font-semibold mb-1">
                  Description
                  <span style="color: var(--p-text-muted-color, #6b7280)">
                    {{ h.DescriptionDiffers ? 'differs from the manufacturer' : 'matches the manufacturer' }}
                  </span>
                </div>
                <div *ngIf="h.DescriptionDiffers" class="text-sm">
                  The manufacturer calls it &ldquo;{{ h.FeedDescription }}&rdquo;.
                </div>
              </div>
            </div>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template pTemplate>
      <div class="flex flex-row w-full h-full ml-4 gap-4">
        <!-- Left side: vertically stacked buttons -->
        <div class="flex flex-col gap-2 pt-24 shrink-0">
          <button
            [disabled]="!canOpenProductGenerator"
            (click)="addProducts()"
            pButton
            type="button"
            class="p-button-raised p-button-sm"
            icon="pi pi-plus"
          >
            <span>Go to Product Generator</span>
          </button>
          <div
            *ngIf="productGeneratorBlockedReason"
            class="text-xs -mt-1 max-w-48"
            style="color: var(--p-text-muted-color, #6b7280)"
          >
            {{ productGeneratorBlockedReason }}
          </div>
          <button
            [disabled]="isAdding || !user.isProductAdmin"
            (click)="addAltProductType()"
            pButton
            type="button"
            class="p-button-raised p-button-sm"
            icon="pi pi-link"
          >
            <span>Attach Alternate Product Types</span>
          </button>
        </div>
        <!-- Right side: tab view with grid -->
        <div class="flex flex-col w-1/2 h-full">
          <p-tabView class="flex-1 min-h-0 qb-tabview" (onChange)="onTabChanged($event)">
            <p-tabPanel header="Available Products" class="h-full">
              <div class="flex flex-col h-full">
                <div class="qb-child-actions shrink-0">
                  <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                      <p-checkbox
                        [disabled]="!user.isProductAdmin"
                        [(ngModel)]="allowCombination"
                        inputId="allowCombination"
                        [ngModelOptions]="{ standalone: true }"
                        [binary]="true"
                        (ngModelChange)="onAllowCombinationChange()"
                      />
                      <label for="allowCombination">Allow Combination/Toggle Status</label>
                    </div>
                    <div class="flex items-center gap-2">
                      <p-checkbox
                        [(ngModel)]="shouldShowInactiveProducts"
                        inputId="shouldShowInactiveProducts"
                        [ngModelOptions]="{ standalone: true }"
                        [binary]="true"
                        (ngModelChange)="onShouldShowInactiveProductsChange()"
                      />
                      <label for="shouldShowInactiveProducts">Show Inactive</label>
                    </div>
                  </div>
                </div>
                <app-ag-grid
                  [gridOptions]="productGridOptions"
                  [rowData]="products"
                  [rowSelection]="rowSelection"
                  class="flex-1 min-h-0"
                ></app-ag-grid>
              </div>
            </p-tabPanel>
            <p-tabPanel header="Alternate Product Types" class="h-full">
              <app-ag-grid [gridOptions]="altItsGridOptions" [rowData]="altProductTypes" class="h-full"></app-ag-grid>
            </p-tabPanel>
          </p-tabView>
        </div>
      </div>
    </ng-template>
  </p-splitter>
</ng-template>

<ng-template #footerTemplate>
  <div class="left-buttons flex flex-row gap-2">
    <button
      *ngIf="uow.hasChanges()"
      (click)="saveChanges()"
      [disabled]="isCheckingStyle || isStyleDuplicate"
      pButton
      type="button"
      class="p-button-raised"
      icon="pi pi-check"
    >
      <span>Save</span>
    </button>
    <button *ngIf="uow.hasChanges()" (click)="onCancel()" pButton type="button" class="p-button-raised" icon="pi pi-undo">
      <span>Undo</span>
    </button>
    <button *ngIf="!uow.hasChanges()" (click)="onCancel()" pButton type="button" class="p-button-raised" icon="pi pi-arrow-left">
      <span>Go Back</span>
    </button>
  </div>
</ng-template>
